import React from "react"
import PropTypes from "prop-types"
import I18n from "lib/i18n"
import { CircleUserRound } from "lucide-react"
import { menuLinkStyle } from "../AccountMenu/shared"
import ActionIcon from "../ActionIcon"
import clsx from "classnames"
import DashboardLink from "../AccountMenu/DashboardLink"

// Paths that are only useful to a signed-out visitor. Sending someone back to
// one of these after they authenticate either shows them a form they no longer
// need or bounces them straight back out again, so we leave return_to off and
// let the server pick a sensible landing page instead. Keep in step with
// ApplicationController#unreturnable_path?, which is the server-side backstop.
const UNRETURNABLE_PATHS = ["/sign_in", "/sign_up", "/users/password"]

const returnToPath = () => {
  const pathname = window && window.location && window.location.pathname

  if (!pathname || UNRETURNABLE_PATHS.some((path) => pathname.startsWith(path))) {
    return ""
  }

  return `?return_to=${pathname}`
}

const UnauthenticatedActions = () => (
  <div className="flex h-full">
    <div className="hidden h-full md:inline-flex">
      <a
        className={clsx(menuLinkStyle, "border-0 border-r border-solid border-gray-200")}
        href="/become-an-author"
      >
        {I18n.t("top_bar.label.author_sign_up")}
      </a>
    </div>
    <div className="hidden h-full md:inline-flex">
      <a
        className={clsx(menuLinkStyle, "border-0 border-r border-solid border-gray-200")}
        href={`/sign_up${returnToPath()}`}
      >
        {I18n.t("top_bar.label.reader_sign_up")}
      </a>
    </div>
    <a
      className={clsx(
        menuLinkStyle,
        "border-0 border-solid border-gray-200 max-sm:px-2! max-sm:py-0! lg:border-r",
      )}
      href={`/sign_in${returnToPath()}`}
    >
      <CircleUserRound className="size-6 md:hidden" aria-label={I18n.t("top_bar.label.sign_in")} />
      <span className="hidden md:inline-flex">{I18n.t("top_bar.label.sign_in")}</span>
    </a>
  </div>
)

const TopbarAdminShortcuts = ({ adminActions }) => {
  const firstAction = adminActions[0]

  if (!firstAction) {
    return null
  }

  return (
    <div data-testid="admin-shortcuts" className="hidden h-full focus-within:z-20 md:inline-flex">
      <a
        href={firstAction.path}
        className={clsx(menuLinkStyle, "border-0 border-r border-solid border-gray-200")}
      >
        {firstAction.icon ? <ActionIcon small name={firstAction.icon} /> : null}
        {firstAction.label}
      </a>
    </div>
  )
}

const TopbarViewerActions = ({ adminActions, user = undefined, userRole }) => {
  if (user) {
    return (
      <React.Fragment>
        <TopbarAdminShortcuts adminActions={adminActions} />
        <div className="hidden h-full border-0 border-r border-solid border-gray-200 focus-within:z-20 md:inline-flex">
          <DashboardLink user={user} userRole={userRole} />
        </div>
      </React.Fragment>
    )
  }

  return <UnauthenticatedActions />
}

TopbarViewerActions.propTypes = {
  user: PropTypes.shape({
    id: PropTypes.string.isRequired,
  }),
  userRole: PropTypes.string,
  adminActions: PropTypes.arrayOf(
    PropTypes.shape({
      path: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
      icon: PropTypes.string,
    }),
  ),
}

TopbarAdminShortcuts.propTypes = {
  adminActions: PropTypes.arrayOf(
    PropTypes.shape({
      path: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
      icon: PropTypes.string,
    }),
  ).isRequired,
}

export default TopbarViewerActions
